/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, NodeTag } from '@msdyn365-commerce-modules/utilities';
import MsDyn365 from '@msdyn365-commerce/core';
import { ICategoryUrl } from '@msdyn365-commerce/core-internal/dist/types/interfaces/ICategoryPathInterfaces';
import classnames from 'classnames';
import * as React from 'react';
import { getVirtualRefinerNames, navLinkKeywordRemoval, regExSpecialChars, removeKeywords } from '../../Utilities/plp-url-utils';
import { Link } from './components/link';
import { Separator } from './components/separator';
import { IMfrmBreadcrumbData } from './mfrm-breadcrumb.data';
import { ILinksData, IMfrmBreadcrumbProps } from './mfrm-breadcrumb.props.autogenerated';
import { parseBrandName } from '../../Utilities/get-product-brand';

export interface IBreadcrumbViewProps extends IMfrmBreadcrumbProps<IMfrmBreadcrumbData> {
    root?: React.ReactNode;
    isLoadedRoot?: boolean;
    links?: React.ReactNode[];
    backButton?: React.ReactNode;
    separator?: React.ReactNode;
    BreadcrumbContainer: IModuleProps;
    BreadcrumbList: INodeProps;
    BreadcrumbListItem: INodeProps;
    plpBreadcrumbSchema: string;
    pdpBreadcrumbSchema: string;
}
interface ICategory {
    Name: string;
    Url: string;
}
interface IConcatRefiners {
    concatRefinerCat: ICategory[];
}
interface IUrlParts {
    realUrlParts: string[];
    refinerParts: string[];
}
interface IBreadcrumbSchemaState {
    plpBreadcrumbSchema: string;
}
interface IRootState {
    isLoadedRoot: boolean;
}
/**
 *
 * Breadcrumb component
 * @extends {React.PureComponent<IMfrmBreadcrumbProps<IMfrmBreadcrumbData>>}
 */
class Breadcrumb extends React.PureComponent<IMfrmBreadcrumbProps<IMfrmBreadcrumbData>, IConcatRefiners & IBreadcrumbSchemaState & IRootState> {
    private _pageType: string | undefined = this.props.context.request.urlTokens.pageType;
    private telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );
    constructor(props: IMfrmBreadcrumbProps<IMfrmBreadcrumbData>) {
        super(props);
        this.state = {
            concatRefinerCat: [],
            plpBreadcrumbSchema: '',
            isLoadedRoot: false
        };
    }
    public componentDidMount(): void {
        const categoryHierarchyTemp: ICategory[] = [];
        const { part, realUrlParts } = this._getUrlString();
        part &&
            part.split('/').forEach((st, index) => {
                const originalUrlPart = realUrlParts && realUrlParts[index];
                const url = this._getUrl(originalUrlPart);
                const refinerCat: ICategory = {
                    Name: this._trimSpacesAndCapitalize(st.split('$$').join('/')),
                    Url: url
                };
                categoryHierarchyTemp.push(refinerCat);
            });
        this.setState({ concatRefinerCat: categoryHierarchyTemp });
    }
    public componentDidUpdate(): void {
        const itemPosition = this._getRootLink() ? 1 : 0;
        const schema = this._makePlpBreadcrumbSchema(itemPosition);
        if (schema) {
            this.setState({
                plpBreadcrumbSchema: schema
            });
        }
    }
    public render(): JSX.Element | null {
        const { className } = this.props.config;
        let rootLink = null;
        let breadcrumbLinks = null;
        let backButtonComponent = null;
        rootLink = this._getRootLink();
        const itemPosition = rootLink ? 1 : 0;
        if (this._pageType === 'Category') {
            breadcrumbLinks = this._getCustomBreadcrumbLinks(itemPosition);
            if (!breadcrumbLinks) {
                breadcrumbLinks = this._getCategoryHierarchy(itemPosition);
            }
        } else if (this._pageType === 'Product') {
            breadcrumbLinks = this._getCustomBreadcrumbLinks(itemPosition);
            if (!breadcrumbLinks) {
                const breaCrumbType = this.props.context.app.config.breadcrumbType;
                if (breaCrumbType === 'category') {
                    breadcrumbLinks = this._getPDPLinks(itemPosition);
                }
                if (breaCrumbType === 'categoryAndBack') {
                    backButtonComponent = this._getPDPBackButton();
                    breadcrumbLinks = this._getPDPLinks(itemPosition);
                }
                if (breaCrumbType === 'back') {
                    backButtonComponent = this._getPDPBackButton();
                    if (backButtonComponent === null) {
                        breadcrumbLinks = this._getPDPLinks(itemPosition);
                    }
                }
            }
        } else {
            breadcrumbLinks = this._getCustomBreadcrumbLinks(itemPosition);
        }
        const linkSeparator = <Separator separator='/' />;
        const breadcrumbViewProps = {
            ...this.props,
            BreadcrumbContainer: {
                moduleProps: this.props,
                className: classnames('ms-breadcrumb', className),
                tag: 'nav',
                role: 'navigation'
            },
            BreadcrumbList: {
                tag: 'ol' as NodeTag,
                className: 'ms-breadcrumb_list'
            },
            BreadcrumbListItem: {
                tag: 'li',
                className: 'ms-breadcrumb_item'
            },
            root: rootLink,
            isLoadedRoot: this.state.isLoadedRoot,
            links: breadcrumbLinks,
            backButton: backButtonComponent,
            separator: linkSeparator,
            plpBreadcrumbSchema: this.state.plpBreadcrumbSchema ? this.state.plpBreadcrumbSchema : null,
            pdpBreadcrumbSchema: this._makePdpBreadcrumbSchema(itemPosition)
        };
        return this.props.renderView(breadcrumbViewProps) as React.ReactElement;
    }
    private _getRootLink = (): JSX.Element | null => {
        const { root } = this.props.config;
        if (!root || !root.linkUrl || !root.linkUrl.destinationUrl) {
            this.props.context.telemetry.error('BreadCrumb link url content is empty, link wont render');
            return null;
        }
        this.setState({
            isLoadedRoot: true
        });
        return (
            <Link
                text={root.linkText}
                ariaLabel={root.ariaLabel || root.linkText}
                href={navLinkKeywordRemoval(root.linkUrl && root.linkUrl.destinationUrl, this.props.context.request)}
                index='1'
                className='ms-breadcrumb_link'
                telemetryContent={this.telemetryContent}
            />
        );
    };

    private _makePlpBreadcrumbSchema(itemPosition: number): string | null {
        const homeUrl = this.props.context.request.url.requestUrl.origin;
        const { root } = this.props.config;
        const plpLinks = this._getCategoryHierarchy(itemPosition);
        let position = 1;
        const breadcrumbData = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [{
                "@type": "ListItem",
                "position": position++,
                "item": {
                    "id": homeUrl,
                    "name": root?.linkText
                }
            }]
        };
        if (this._pageType === 'Category') {
            plpLinks?.map(plpLink => {
                const breadcrumbUrl = homeUrl.concat(plpLink.props.href);
                const modifiedBreadcrumbUrl = this.modifyBreadcrumbUrl(breadcrumbUrl);
                breadcrumbData.itemListElement.push({
                    "@type": "ListItem",
                    "position": position++,
                    "item": {
                        "id": modifiedBreadcrumbUrl,
                        "name": plpLink.props.text
                    }
                });
            });
        }
        else {
            const searchQueryValue = this.props.context.request.query?.q;
            breadcrumbData.itemListElement.push({
                "@type": "ListItem",
                "position": position++,
                "item": {
                    "id": `${homeUrl}/${searchQueryValue}`,
                    "name": searchQueryValue && this._trimSpacesAndCapitalize(searchQueryValue)
                }
            });
        }
        // Construct the JSON-LD data that contains the product metadata information used by search enginge crawlers
        const plpBreadcrumbSchemaData = JSON.stringify(breadcrumbData);
        return plpBreadcrumbSchemaData;
    }
    private _makePdpBreadcrumbSchema(itemPosition: number): string | null {
        const homeUrl = this.props.context.request.url.requestUrl.origin;
        const { root } = this.props.config;
        const pdpLinks = this._getPDPLinks(itemPosition);
        const breadcrumbLinks = this._getCustomBreadcrumbLinks(itemPosition);
        let position = 1;
        const breadcrumbData = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [{
                "@type": "ListItem",
                "position": position++,
                "item": {
                    "id": homeUrl,
                    "name": root?.linkText
                }
            }]
        };
        if (this._pageType === 'Product') {
            pdpLinks?.map(pdpLink => {
                const pdpBreadcrumbUrl = pdpLink.props.href.includes(homeUrl) ? pdpLink.props.href : homeUrl.concat(pdpLink.props.href);
                const modifiedBreadcrumbUrl = this.modifyBreadcrumbUrl(pdpBreadcrumbUrl);
                breadcrumbData.itemListElement.push({
                    "@type": "ListItem",
                    "position": position++,
                    "item": {
                        "id": modifiedBreadcrumbUrl,
                        "name": pdpLink.props.text
                    }
                });
            });
        }
        else {
            breadcrumbLinks?.map(breadcrumbLink => {
                breadcrumbData.itemListElement.push({
                    "@type": "ListItem",
                    "position": position++,
                    "item": {
                        "id": breadcrumbLink.props.href,
                        "name": breadcrumbLink.props.text
                    }
                });
            });
        }
        // Construct the JSON-LD data that contains the product metadata information used by search enginge crawlers
        const pdpBreadcrumbSchemaData = JSON.stringify(breadcrumbData);
        return pdpBreadcrumbSchemaData;
    }
    private modifyBreadcrumbUrl = (url: string): string => {
        const urlParts = url.split('\/');
        urlParts.pop();
        const newUrl = urlParts?.join('\/');
        return newUrl;
    };
    private _getCustomBreadcrumbLinks = (itemPosition: number): JSX.Element[] | null => {
        const { links } = this.props.config;

        if (!links || !(links.length > 0)) {
            this.props.context.telemetry.error('BreadCrumb links content is empty, links wont render');
            return null;
        }
        return links.map((value: ILinksData, index: number) => {
            itemPosition++;
            return (
                <Link
                    key={index}
                    text={value.linkText}
                    ariaLabel={value.ariaLabel || value.linkText}
                    href={navLinkKeywordRemoval(value.linkUrl && value.linkUrl.destinationUrl, this.props.context.request)}
                    index={{ itemPosition }.toString()}
                    className='ms-breadcrumb_link'
                    telemetryContent={this.telemetryContent}
                />
            );
        });
    };
    private _makeRealUrlParts = (decodedArr: string[] | false | ''): IUrlParts => {
        let refinerParts: string[] = [];
        let realUrl = '';
        const realUrlParts: string[] = [];
        decodedArr &&
            decodedArr.forEach((part, index) => {
                switch (index) {
                    case 0:
                        realUrl = part;
                        break;
                    case 1:
                        realUrl = `${part}`;
                        break;
                    case 2:
                        realUrl = `${part}`;
                        break;
                    default:
                        realUrl = '';
                }
                realUrlParts.push(realUrl);
                if (part.includes('|')) {
                    refinerParts = [...refinerParts, ...part.split('|')];
                } else {
                    refinerParts = [...refinerParts, part];
                }
            });
        return { realUrlParts, refinerParts };
    };
    private _getUrlString = (): { part: string; realUrlParts: string[] } => {
        const { data, context } = this.props;
        const requestContext = context && context.request;
        const categoryIdToken = requestContext.urlTokens.recordId;
        const categoryId = categoryIdToken ? `${categoryIdToken}.c` : undefined;
        let slug = (data.category.result && data.category.result.Slug) || '/search';
        let slugArray = slug.split('/').splice(1);
        // remove keywords form slugArray
        slugArray = removeKeywords(slugArray, context.request);
        // FIX reset slug to without keywords
        slug = slugArray.join('/');
        const params = MsDyn365.isBrowser && window.location;
        const refinersString = params && params.pathname.replace(`/${slug}/`, '').replace(`/${categoryId}`, '');
        const decodedArr = refinersString && decodeURIComponent(refinersString).split('/');
        const { realUrlParts, refinerParts } = this._makeRealUrlParts(decodedArr);
        const configVirtualRefinerNames = getVirtualRefinerNames(this.props.context.request, true);
        const filteredRefiners =
            data.refiners.result &&
            data.refiners.result.filter(refiner => {
                return refiner.KeyName && configVirtualRefinerNames.indexOf(refiner.KeyName.toLowerCase()) !== -1;
            });
        const sortedValues: { [key: string]: string[] } = {};
        filteredRefiners?.forEach(refinerSet => {
            let refinerName = refinerSet.KeyName;
            if (refinerName) {
                refinerName = refinerName.toLowerCase();
                sortedValues[refinerName] = []; // size, brand, price
            }
            refinerSet.Values.forEach(refiner => {
                refinerParts &&
                    refinerParts.forEach(refinerPart => {
                        if (refinerName && refiner.LeftValueBoundString &&
                            (refiner.LeftValueBoundString.toLowerCase().replace(/\&/g, 'and').replace(regExSpecialChars(requestContext.app.config.canonicalUrlspecialCharIncludeList), '-') === refinerPart.toLowerCase().split('-').join(' ')
                                || refiner.LeftValueBoundString.toLowerCase().replace(/\&/g, 'and').replace(regExSpecialChars(requestContext.app.config.canonicalUrlspecialCharIncludeList), '-') === refinerPart.toLowerCase())) {
                            sortedValues[refinerName].push(refiner.LeftValueBoundString.split('/').join('$$'));
                        } else if (refinerName && refiner.LeftValueBoundString &&
                            refiner.LeftValueBoundString.toLowerCase() === refinerPart.toLowerCase().split('-').join(' ')) {
                            sortedValues[refinerName].push(refiner.LeftValueBoundString.split('/').join('$$'));
                        }
                    });
            });
        });

        // sort by configuration

        let str = '';
        let strNext = '';
        let breakAll = true;
        const virtualRefiners = [...configVirtualRefinerNames];
        configVirtualRefinerNames.forEach(item => {
            if (!sortedValues[item] || sortedValues[item]?.length < 1) {
                delete sortedValues[item];
                virtualRefiners.splice(virtualRefiners.indexOf(item), 1);
            }
        });

        virtualRefiners.every(item => {
            const sortedRefiners = sortedValues;
            const refiner = item;
            sortedRefiners[refiner]?.length < 1 ? delete sortedRefiners[refiner] : null;
            if (sortedRefiners[refiner]?.length < 2 && breakAll) {
                if (refiner === virtualRefiners[0] && sortedRefiners[refiner]?.length === 1) {
                    str = `${sortedRefiners[refiner]}`;
                }
                if (refiner === virtualRefiners[1] && sortedRefiners[refiner]?.length === 1) {
                    strNext = `${sortedRefiners[virtualRefiners[0]] ? `/ ${sortedRefiners[refiner]}` : `${sortedRefiners[refiner]}`}`;
                    if (sortedRefiners[virtualRefiners[2]]?.length !== 1) {
                        str = !sortedRefiners[virtualRefiners[0]] ? `${sortedRefiners[refiner]}` : `${str} / ${sortedRefiners[refiner]}`;
                    }
                }
                if (refiner === virtualRefiners[2] && sortedRefiners[refiner]?.length === 1) {
                    str =
                        str || strNext
                            ? `${str} ${sortedRefiners[virtualRefiners[1]] ? strNext : '/'} ${sortedRefiners[virtualRefiners[2]] ? `/ ${sortedRefiners[refiner]}` : str
                            }`
                            : `${sortedRefiners[refiner]}`;
                }
                return true;
            } else {
                breakAll = false;
                return false;
            }
        });
        return { part: str, realUrlParts };
    };

    private _getUrl = (originalUrlPart: string): string => {
        const { data, context } = this.props;
        const requestContext = context && context.request;
        const categoryIdToken = requestContext.urlTokens.recordId;
        const categoryId = categoryIdToken ? `${categoryIdToken}.c` : undefined;
        let slug = (data.category.result && data.category.result.Slug) || '/search';
        let slugArray = slug.split('/').splice(1);
        // remove keywords form slugArray
        slugArray = removeKeywords(slugArray, context.request);
        // FIX reset slug to without keywords
        slug = slugArray.join('/');
        const params = MsDyn365.isBrowser && window.location;
        return `/${slug}/${originalUrlPart}${categoryId ? `/${categoryId}` : ''}${params && params.search ? params.search : ''}`;
    };
    private _trimSpacesAndCapitalize = (st: string): string => {
        return st
            .trimStart()
            .trimEnd()
            .replace(/(^\w{1})|(\s{1}\w{1})/g, match => match.toUpperCase());
    };
    private _getCategoryHierarchy = (itemPosition: number): JSX.Element[] | null => {
        const { data } = this.props;
        const categoryHierarchy = data.categoryHierarchy.result && [...data.categoryHierarchy.result];
        if (!categoryHierarchy) {
            this.props.context.telemetry.error('Category Hierarchy content is empty, module wont render');
            return null;
        }
        const categoryHierarchyTemp: ICategory[] = [];
        categoryHierarchy.map(category => {
            const cat: ICategory = {
                Name: category.Name!,
                Url: category.Url!
            };
            categoryHierarchyTemp.push(cat);
        });
        this.state.concatRefinerCat.length &&
            this.state.concatRefinerCat.map((item: ICategory) => {
                categoryHierarchyTemp.push(item);
            });
        return categoryHierarchyTemp
            ? categoryHierarchyTemp.map((value: ICategory, index: number) => {
                itemPosition++;
                return (
                    <Link
                        key={index}
                        text={value.Name}
                        ariaLabel={value.Name}
                        href={navLinkKeywordRemoval(value.Url, this.props.context.request)}
                        index={itemPosition.toString()}
                        className='ms-breadcrumb_link'
                        noclick={index === categoryHierarchyTemp.length - 1}
                        telemetryContent={this.telemetryContent}
                    />
                );
            })
            : null;
    };

    // This is used in _getCategoryHierarchy OOB to support translations. TODO: decide if we want to implement.

    // private readonly _getFriendlyName = (locale: string, nameTranslations?: TextValueTranslation[]): string | undefined => {
    //     let nameTranslation: TextValueTranslation | undefined;
    //     if (locale && nameTranslations && ArrayExtensions.hasElements(nameTranslations)) {
    //         nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
    //     }

    //     // If friendly name for current locale is null, should use default locale.
    //     if (nameTranslation === undefined && ArrayExtensions.hasElements(nameTranslations)) {
    //         nameTranslation = nameTranslations[0];
    //     }

    //     return nameTranslation?.Text;
    // };

    private _getPDPLinks = (itemPosition: number): JSX.Element[] | null => {
        const url = this.props.context.request.url.requestUrl.href;
        const {
            config: { hideProductName },
            data: {
                product: { result: product },
                productSpecificationData: { result: productSpecificationData },
                categoryPaths: { result: categoryHierarchy }

            },
            app: { config: { brandBackofficeAttributePdp } }
        } = this.props;
        const brandName = parseBrandName(productSpecificationData, brandBackofficeAttributePdp) || '';
        if (!categoryHierarchy) {
            this.props.context.telemetry.error('Category path content is empty for the product');
            return null;
        }
        const links: JSX.Element[] = [];

        categoryHierarchy.map((value: ICategoryUrl, index: number) => {
            itemPosition++;
            links.push(
                <Link
                    key={index}
                    text={value.Name}
                    ariaLabel={value.Name}
                    href={navLinkKeywordRemoval(value?.Url!, this.props.context.request)}
                    index={itemPosition.toString()}
                    className='ms-breadcrumb_link'
                    telemetryContent={this.telemetryContent}
                />
            );
        });
        if (product && !hideProductName) {
            itemPosition++;
            links.push(
                <Link
                    text={brandName ? `${brandName} ${product.Name}` : product.Name}
                    ariaLabel={brandName ? `${brandName} ${product.Name}` : product.Name}
                    href={url}
                    index={itemPosition.toString()}
                    noclick={true}
                    className='ms-breadcrumb_link'
                    telemetryContent={this.telemetryContent}
                />
            );
        }
        return links;
    };

    private _goBack = (): void => {
        if (MsDyn365.isBrowser) {
            window.history.back();
        }
    };

    private _getPDPBackButton = (): JSX.Element | null => {
        const isBackAllowed =
            this.props.context && this.props.context.request && this.props.context.request.query && this.props.context.request.query.back;
        if (!isBackAllowed) {
            return null;
        }
        if (MsDyn365.isBrowser && (document.referrer === '' || window.history.length < 2)) {
            return null;
        }
        return (
            <a
                className='ms-breadcrumb-back-button'
                role='button'
                href='javascript:void(0);'
                onClick={this._goBack}
                aria-label={this.props.resources.backToResultsText}
            >
                {this.props.resources.backToResultsText}
            </a>
        );
    };
}

export default Breadcrumb;
